import React, { ReactElement, useEffect } from 'react';
import { useHistory } from 'react-router';
import './TinyMCEContent.scss';

interface TinyMCEContentProps {
    content: string;
    loadScripts?: boolean;
}

export function TinyMCEContent({ content, loadScripts = false }: TinyMCEContentProps): ReactElement {
    const { replace } = useHistory();

    useEffect(() => {
        if (!loadScripts) {
            return;
        }
        window.requestAnimationFrame(() => {
            loadSrcScripts(content);
            loadInlineScripts(content);
        });
    }, [content]);

    const onClick = React.useCallback((event: React.MouseEvent): void => {
        const target = event.target as HTMLElement;
        if (target.tagName !== 'A') {
            return;
        }
        const href = target.getAttribute('href');
        if (!href || !href.startsWith('#')){
            return;
        }
        const id = href.substring(1);
        const scrollTarget = document.getElementById(id);
        if (!scrollTarget) {
            return;
        }
        event.preventDefault();
        scrollTarget.scrollIntoView({ behavior: 'smooth' });
        replace(href);
    }, []);

    return (
        <div className='TinyMCEContent'>
            <div className='html' dangerouslySetInnerHTML={{ __html: content }} onClick={onClick} />
        </div>
    );
}

const srcScriptRegex = /<script\b[^>]*src=['"]([^'"]+)['"][^>]*><\/script>/gi;
const inlineScriptRegex = /<script\b[^>]*>([\s\S]*?)<\/script>/gi;
const loadedScripts: Record<number, boolean> = {};

function loadSrcScripts(content: string): void {
    let match;
    while ((match = srcScriptRegex.exec(content)) !== null) {
        const src = match[1];
        const key = hash(src);
        if (loadedScripts[key]) {
            continue;
        }
        loadedScripts[key] = true;
        const el = document.createElement('script');
        el.async = true;
        el.src = src;
        document.body.appendChild(el);
    }
}

function loadInlineScripts(content: string): void {
    let match;
    while ((match = inlineScriptRegex.exec(content)) !== null) {
        const code = match[1].trim();
        const key = hash(code);
        if (loadedScripts[key]) {
            continue;
        }
        loadedScripts[key] = true;
        const el = document.createElement('script');
        el.type = 'text/javascript';
        el.text = code;
        document.body.appendChild(el);
    }
}

function hash(str: string): number {
    let hash = 0;
    if (str.length === 0) {
        return hash;
    }
    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash |= 0; // convert to 32bit integer
    }
    return hash;
}
